import type { MapFeedItemDTO, TrackLite } from "../data/types";

/**
 * Vad ett ställe säger om musiken, för kartkortet och venue-sidan.
 *
 * Backend räknar `status` ur senaste spelningen oavsett plan och sensor, så
 * ett ställe utan live-data kan ha status LIVE. Det är inget löfte om vad
 * som spelas nu, och ingen vy får påstå det: utan live-data visas varken
 * låt eller ålder, bara att live-data saknas. Native-appen har samma regel
 * (utils/venueStatus.ts där).
 */
export type MusicStatus =
  /** Stället delar inte vad som spelas. Visa ingen låt och ingen ålder. */
  | { kind: "noLiveData" }
  | { kind: "live"; track: TrackLite }
  | { kind: "recent"; track: TrackLite; age: string | null }
  /** Live-data finns men ingen låt. */
  | { kind: "silent" };

type MusicInput = Pick<
  MapFeedItemDTO,
  "hasLiveData" | "status" | "nowPlaying" | "ageMinutes"
>;

export function ageLabel(ageMinutes: number) {
  if (ageMinutes <= 0) return "just now";
  if (ageMinutes < 60) return `${ageMinutes} min ago`;
  const h = Math.floor(ageMinutes / 60);
  if (h < 24) return `${h} hours ago`;
  const d = Math.floor(h / 24);
  return `${d} days ago`;
}

export function musicStatus(item: MusicInput): MusicStatus {
  if (item.hasLiveData === false) return { kind: "noLiveData" };
  if (!item.nowPlaying) return { kind: "silent" };
  if (item.status === "LIVE") return { kind: "live", track: item.nowPlaying };

  return {
    kind: "recent",
    track: item.nowPlaying,
    age: item.ageMinutes == null ? null : ageLabel(item.ageMinutes),
  };
}

/** Kort rad under ställets namn. */
export function musicStatusLine(status: MusicStatus): string {
  switch (status.kind) {
    case "noLiveData":
      return "No live data";
    case "live":
      return "LIVE";
    case "recent":
      return status.age ? `Played ${status.age}` : "Played earlier";
    case "silent":
      return "Nothing played yet";
  }
}

/** Rubriken ovanför låten. Null när ingen låt ska visas. */
export function musicHeading(status: MusicStatus): string | null {
  switch (status.kind) {
    case "live":
      return "Is currently playing";
    case "recent":
      return "Recently played";
    default:
      return null;
  }
}
